import { useCallback, useRef, useState, type ReactNode, useEffect } from 'react'
import { Modal, Dialog as AriaDialog, ModalOverlay } from 'react-aria-components'
import { twJoin } from 'tailwind-merge'

import { useIsWideLayout } from '@hooks/useMediaQuery.ts'

import { DialogContext, type BaseDialogProps, type DialogComponent, type DialogContextValue } from './DialogContext'

interface DialogProviderProps {
  children: ReactNode
}

interface ActiveDialog {
  Component: DialogComponent<any>
  props: Record<string, any>
}

export function DialogProvider({ children }: DialogProviderProps) {
  const isWideLayout = useIsWideLayout()
  const [activeDialog, setActiveDialog] = useState<ActiveDialog | null>(null)
  const [preventBackdropClose, setPreventBackdropClose] = useState(false)

  // Keep a ref to the last dialog for rendering during exit animations
  const lastDialogRef = useRef<ActiveDialog | null>(null)

  // Update the ref when activeDialog changes
  useEffect(() => {
    if (activeDialog !== null) {
      lastDialogRef.current = activeDialog
    }
  }, [activeDialog])

  // Always render the last known dialog (either current or last for exit animation)

  const dialogToRender = activeDialog ?? lastDialogRef.current

  const showDialog = useCallback(
    <T extends BaseDialogProps>(Component: DialogComponent<T>, props?: Omit<T, 'onClose'>) => {
      const dialogProps = props ?? ({} as Omit<T, 'onClose'>)
      setActiveDialog({ Component, props: dialogProps })
    },
    []
  )

  const hideDialog = useCallback(() => {
    setActiveDialog(null)
    setPreventBackdropClose(false)
  }, [])

  const contextValue: DialogContextValue = {
    showDialog,
    hideDialog,
    isOpen: activeDialog !== null,
    setPreventBackdropClose,
  }

  return (
    <DialogContext.Provider value={contextValue}>
      {children}
      <ModalOverlay
        isOpen={activeDialog !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen && !preventBackdropClose) {
            hideDialog()
          }
        }}
        isDismissable={!preventBackdropClose}
        className="backdrop backdrop-blur-sm"
      >
        <Modal className={twJoin('relative w-full outline-hidden', !isWideLayout && 'drawer-transitions')}>
          <AriaDialog className="outline-hidden">
            {({ close }) => {
              const DialogComponent = dialogToRender?.Component
              const dialogProps = dialogToRender?.props

              return DialogComponent ? <DialogComponent {...dialogProps} onClose={close} /> : null
            }}
          </AriaDialog>
        </Modal>
      </ModalOverlay>
    </DialogContext.Provider>
  )
}
